/**
 * Bridge to Electron main-process Realm, or an in-memory stub for browser testing.
 */

import { isElectronRuntime, isRealmEnabled } from './isRealmEnabled';

const memory = {
  open: false,
  userId: null,
  meta: new Map(),
  collections: new Map(),
};

const ensureCollection = (type) => {
  if (!memory.collections.has(type)) {
    memory.collections.set(type, new Map());
  }
  return memory.collections.get(type);
};

const idOf = (doc) => doc?._id || doc?.id || null;

const normalizeDoc = (doc) => {
  if (!doc || typeof doc !== 'object') return doc;
  const out = { ...doc };
  const id = idOf(out);
  if (id != null) {
    out.id = id;
    out._id = id;
  }
  return out;
};

const memoryApi = {
  isElectron: async () => false,
  isOpen: async () => memory.open,
  currentUserId: async () => memory.userId,
  open: async (userId) => {
    memory.open = true;
    memory.userId = userId;
    return { userId, path: 'memory' };
  },
  close: async ({ wipe = false } = {}) => {
    memory.open = false;
    memory.userId = null;
    if (wipe) {
      memory.meta.clear();
      memory.collections.clear();
    }
    return { closed: true };
  },
  applyBootstrap: async (bootstrap) => {
    const map = [
      ['Channel', bootstrap?.channels],
      ['Message', bootstrap?.messages],
      ['UserProfile', bootstrap?.users],
      ['Task', bootstrap?.tasks],
      ['Reminder', bootstrap?.reminders],
      ['SavedMessage', bootstrap?.savedMessages],
      ['UserChannelView', bootstrap?.userChannelViews],
      ['ChannelMembership', bootstrap?.channelMemberships],
      ['AttendanceEvent', bootstrap?.attendanceEvents],
      ['AttendanceShare', bootstrap?.attendanceShares],
      ['ProductivityEntry', bootstrap?.productivityEntries],
      ['Appointment', bootstrap?.appointments],
      ['ResourceGroup', bootstrap?.resourceGroups],
      ['MessageTemplate', bootstrap?.messageTemplates],
      ['Organization', bootstrap?.organizations],
      ['Announcement', bootstrap?.announcements],
      ['OrgWorkflowReaction', bootstrap?.orgWorkflowReactions],
    ];
    const counts = {};
    for (const [type, docs] of map) {
      counts[type] = 0;
      if (!Array.isArray(docs)) continue;
      const col = ensureCollection(type);
      for (const doc of docs) {
        const id = idOf(doc);
        if (!id) continue;
        col.set(id, normalizeDoc(doc));
        counts[type] += 1;
      }
    }
    if (bootstrap?.syncedAt) {
      memory.meta.set('lastSyncedAt', bootstrap.syncedAt);
    }
    if (bootstrap?.userId) {
      memory.meta.set('userId', bootstrap.userId);
    }
    if (Array.isArray(bootstrap?.assignedWorkflows)) {
      memory.meta.set('assignedWorkflows', JSON.stringify(bootstrap.assignedWorkflows));
    }
    return { ok: true, counts };
  },
  upsert: async (type, docs = []) => {
    const col = ensureCollection(type);
    const ids = [];
    for (const doc of docs) {
      const id = idOf(doc);
      if (!id) continue;
      col.set(id, normalizeDoc(doc));
      ids.push(id);
    }
    return { upserted: ids.length, ids };
  },
  delete: async (type, id) => {
    const col = ensureCollection(type);
    const deleted = col.delete(id);
    return { deleted };
  },
  query: async ({ type, filter, args = [], sort, limit } = {}) => {
    const col = ensureCollection(type);
    let list = Array.from(col.values());
    // Minimal filter support: "channelId == $0", "userId == $0"
    if (filter && typeof filter === 'string') {
      const eq = filter.match(/^(\w+)\s*==\s*\$0$/);
      if (eq) {
        const field = eq[1];
        const value = args[0];
        list = list.filter((row) => row[field] === value);
      }
    }
    if (Array.isArray(sort) && sort.length) {
      const [field, desc] = sort[0];
      list = [...list].sort((a, b) => {
        const av = a[field] || '';
        const bv = b[field] || '';
        if (av < bv) return desc ? 1 : -1;
        if (av > bv) return desc ? -1 : 1;
        return 0;
      });
    }
    if (typeof limit === 'number' && limit > 0) {
      list = list.slice(0, limit);
    }
    return list.map(normalizeDoc);
  },
  get: async (type, id) => {
    const col = ensureCollection(type);
    return normalizeDoc(col.get(id) || null);
  },
  setMeta: async (key, value) => {
    memory.meta.set(key, value == null ? null : String(value));
    return { ok: true };
  },
  getMeta: async (key) => memory.meta.get(key) ?? null,
};

const noopApi = {
  isElectron: async () => false,
  isOpen: async () => false,
  currentUserId: async () => null,
  open: async () => ({ skipped: true }),
  close: async () => ({ skipped: true }),
  applyBootstrap: async () => ({ skipped: true }),
  upsert: async () => ({ skipped: true }),
  delete: async () => ({ skipped: true }),
  query: async () => [],
  get: async () => null,
  setMeta: async () => ({ skipped: true }),
  getMeta: async () => null,
};

let realmOpenCached = null;

export const setRealmOpenCached = (open) => {
  realmOpenCached = Boolean(open);
};

export const getRealmBridge = () => {
  if (!isRealmEnabled()) {
    return noopApi;
  }
  if (isElectronRuntime() && window.workloopRealm) {
    return window.workloopRealm;
  }
  return memoryApi;
};

export const realmAvailable = async () => {
  if (!isRealmEnabled()) return false;
  if (realmOpenCached === true) return true;
  const bridge = getRealmBridge();
  try {
    const open = Boolean(await bridge.isOpen());
    if (open) realmOpenCached = true;
    return open;
  } catch {
    return false;
  }
};
